import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Shield, Loader2 } from 'lucide-react';
import {
  DashboardOverview,
  UserManagement,
  ContentManagement,
  CommentModeration,
  VideoManagement,
  AnalyticsDashboard,
  RevenueAnalytics,
  EmailBlasts
} from '../components/admin';
import NotificationManagement from '../components/admin/NotificationManagement';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [content, setContent] = useState([]);
  const [comments, setComments] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [muxVideos, setMuxVideos] = useState([]);
  const [editingVideo, setEditingVideo] = useState(null);
  const [deletingVideoId, setDeletingVideoId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }
    
    if (user && !user.is_admin) {
      navigate('/');
      return;
    }

    loadDashboardData();
  }, [isAuthenticated, user, navigate, authLoading]);

  const loadDashboardData = async () => {
    try {
      const [statsRes, usersRes, contentRes, commentsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users?limit=10'),
        api.get('/admin/content?limit=100'),
        api.get('/admin/comments?flagged_only=true&limit=10')
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setContent(contentRes.data.content);
      setComments(commentsRes.data.comments);
      
      try {
        const muxRes = await api.get('/mux/videos?limit=50');
        setMuxVideos(muxRes.data);
      } catch (err) {
        console.log('Mux videos not available');
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
      if (error.response?.status === 403) {
        navigate('/');
      }
    } finally {
      setDataLoading(false);
    }
  };

  // User handlers
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
    } catch (error) {
      alert('Error deleting user');
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    try {
      await api.put(`/admin/users/${userId}`, { is_admin: !currentStatus });
      setUsers(users.map(u => u.id === userId ? { ...u, is_admin: !currentStatus } : u));
    } catch (error) {
      alert('Error updating user');
    }
  };

  const handleBanCommenting = async (userId, canComment) => {
    try {
      if (canComment) {
        await api.post(`/admin/users/${userId}/ban-commenting`);
      } else {
        await api.post(`/admin/users/${userId}/unban-commenting`);
      }
      setUsers(users.map(u => u.id === userId ? { ...u, can_comment: !canComment } : u));
    } catch (error) {
      alert('Error updating user');
    }
  };

  // Content handlers
  const handleDeleteContent = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    try {
      await api.delete(`/admin/content/${contentId}`);
      setContent(content.filter(c => c.id !== contentId));
    } catch (error) {
      alert('Error deleting content');
    }
  };

  // Comment handlers
  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/admin/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
    } catch (error) {
      alert('Error deleting comment');
    }
  };

  const handleApproveComment = async (commentId) => {
    try {
      await api.post(`/admin/comments/${commentId}/approve`);
      setComments(comments.filter(c => c.id !== commentId));
    } catch (error) {
      alert('Error approving comment');
    }
  };

  // Video handlers
  const handleDeleteVideo = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this video? This will also delete it from Mux.')) return;
    setDeletingVideoId(contentId);
    try {
      await api.delete(`/mux/videos/${contentId}`);
      setMuxVideos(muxVideos.filter(v => v.content_id !== contentId));
      loadDashboardData();
    } catch (error) {
      alert('Error deleting video: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setDeletingVideoId(null);
    }
  };

  const handleAddTrailer = async (contentId) => {
    try {
      const response = await api.post(`/mux/create-trailer-upload/${contentId}`);
      setEditingVideo({ contentId, trailerUploadUrl: response.data.upload_url });
    } catch (error) {
      alert('Error creating trailer upload: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleUploadComplete = (data) => {
    setMuxVideos(prev => [data, ...prev]);
    loadDashboardData();
  };

  if (authLoading || dataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-electric animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center space-x-3">
              <Shield className="h-8 w-8 text-electric" />
              <span>Admin Dashboard</span>
            </h1>
            <p className="text-muted-foreground mt-1">Manage users, content, and moderation</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-8 overflow-x-auto pb-2">
          {['dashboard', 'analytics', 'revenue', 'email', 'users', 'content', 'videos', 'comments', 'notifications'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium capitalize transition-all ${
                activeTab === tab
                  ? 'bg-electric text-background'
                  : 'bg-white/5 text-foreground hover:bg-white/10'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab === 'videos' ? 'Video Upload' : tab === 'email' ? 'Email Blasts' : tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && (
          <DashboardOverview
            stats={stats}
            users={users}
            comments={comments}
            onApproveComment={handleApproveComment}
            onDeleteComment={handleDeleteComment}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsDashboard />
        )}

        {activeTab === 'revenue' && (
          <RevenueAnalytics />
        )}

        {activeTab === 'email' && (
          <EmailBlasts />
        )}

        {activeTab === 'users' && (
          <UserManagement
            users={users}
            onDeleteUser={handleDeleteUser}
            onToggleAdmin={handleToggleAdmin}
            onBanCommenting={handleBanCommenting}
          />
        )}

        {activeTab === 'content' && (
          <ContentManagement
            content={content}
            onDeleteContent={handleDeleteContent}
            onRefreshData={loadDashboardData}
          />
        )}

        {activeTab === 'comments' && (
          <CommentModeration
            comments={comments}
            onApproveComment={handleApproveComment}
            onDeleteComment={handleDeleteComment}
          />
        )}

        {activeTab === 'videos' && (
          <VideoManagement
            muxVideos={muxVideos}
            onDeleteVideo={handleDeleteVideo}
            onAddTrailer={handleAddTrailer}
            onUploadComplete={handleUploadComplete}
            editingVideo={editingVideo}
            setEditingVideo={setEditingVideo}
            deletingVideoId={deletingVideoId}
            onRefreshData={loadDashboardData}
          />
        )}

        {activeTab === 'notifications' && (
          <NotificationManagement />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
